/**
 * CL-PROPERTY-OS-002C3A / 002C3B — Restored-session invite recovery classification.
 */

import {
  validatePortalInviteRecoveryEvidence,
} from "./portalInviteLifecycleValidation";

export const PORTAL_RECOVERY_MODE = Object.freeze({
  NONE: "none",
  SAME_MOUNT: "same_mount",
  FINISH_SETUP: "finish_setup",
  PROPERTY_OS_RETRY: "property_os_retry",
  AWAITING_INVITE: "awaiting_invite",
  INVALID: "invalid",
});

function safeStr(value) {
  return String(value ?? "").trim();
}

export function normalizeRecoveryEmail(value) {
  return safeStr(value).toLowerCase();
}

export function isPendingSignupContextComplete(context = {}) {
  if (!context || typeof context !== "object") return false;
  if (!safeStr(context.inviteCode)) return false;
  if (!safeStr(context.firstName)) return false;
  if (!safeStr(context.lastName)) return false;
  return true;
}

/**
 * Classify recovery without inferring identity from browser state beyond Auth + invite doc.
 */
export function classifyPortalInviteRecovery({
  authUid = "",
  authEmail = "",
  inviteCode = "",
  inviteData = null,
  inviteLoadState = "idle",
  pendingSignupContext = null,
} = {}) {
  if (pendingSignupContext) {
    return { mode: PORTAL_RECOVERY_MODE.SAME_MOUNT };
  }

  const uid = safeStr(authUid);
  const email = normalizeRecoveryEmail(authEmail);
  const code = safeStr(inviteCode);

  if (!uid || !email) {
    return { mode: PORTAL_RECOVERY_MODE.NONE };
  }

  if (!code) {
    return { mode: PORTAL_RECOVERY_MODE.NONE };
  }

  if (inviteLoadState === "loading") {
    return { mode: PORTAL_RECOVERY_MODE.AWAITING_INVITE };
  }

  if (inviteLoadState === "error" || !inviteData) {
    return { mode: PORTAL_RECOVERY_MODE.INVALID, reason: "invite_unavailable" };
  }

  const validation = validatePortalInviteRecoveryEvidence({
    authUser: { uid, email, emailVerified: true },
    inviteData,
    inviteCode: code,
  });
  if (!validation.ok) {
    return { mode: PORTAL_RECOVERY_MODE.INVALID, reason: validation.reason || "invite_unavailable" };
  }
  if (validation.mode === "property_os_retry") {
    return { mode: PORTAL_RECOVERY_MODE.PROPERTY_OS_RETRY };
  }
  if (validation.mode === "finish_setup") {
    return { mode: PORTAL_RECOVERY_MODE.FINISH_SETUP };
  }

  return { mode: PORTAL_RECOVERY_MODE.INVALID, reason: "invite_malformed_used_state" };
}

export function recoveryInvalidMessage(reason = "") {
  switch (safeStr(reason)) {
    case "email_mismatch":
      return "This invite is tied to a different email address. Sign in with the invited email or contact support.";
    case "used_by_other":
      return "This invite was already used by another account. Contact support if you need help.";
    case "invite_malformed_used_state":
      return "This invitation is in an unexpected state. Contact support for assistance.";
    case "invite_unavailable":
      return "We could not load your invitation details. Check the invite link and try again.";
    case "auth_not_verified":
      return "Verify your email before continuing portal setup.";
    case "invite_recipient_missing":
      return "This invitation is missing recipient information. Contact support for assistance.";
    case "invite_invalidated":
      return "This invitation is no longer valid. Contact support for a new invite.";
    case "invite_expired":
      return "This invitation has expired. Contact support for a new invite.";
    case "invite_expiry_malformed":
    case "invite_used_at_missing_or_malformed":
      return "This invitation is in an unexpected state. Contact support for assistance.";
    default:
      return "We could not resume your portal setup. Contact support if this continues.";
  }
}
